/**
 * CierreTicketWebBody.tsx — Cuerpo canónico del ticket de cierre (vista web / imagen).
 *
 * Renderiza el desglose completo del cierre en orden fijo. El padre (`CierreTicketPreview`)
 * envuelve con encabezado de negocio, título y branding.
 *
 * QUÉ INVOCA: buildCierreTicketModel, CierreTicketWebTablas, cierreHelpers,
 * ticketCierreTablaRejilla, helpers de movimientos/creditos.
 *
 * QUÉ EXPORTA: CierreTicketWebBody, loc()
 */

import { Fragment, useMemo, type ReactNode } from "react";
import { getDefaultPaymentMethodLabel } from "@/lib/paymentMethodsConfig";
import {
  TicketTablaBilletesMonedas,
  TicketTablaCajaFisica,
  TicketTablaComparacionArqueo,
  TicketTablaIntentosArqueoCiego,
  TicketTabla2ColGrupos,
  TicketResumenOperativoTurno,
  TicketSeccionTopProductos,
  TicketVerificacionBaseApertura,
  TicketSeccionPropinasCobradas,
  TicketSeccionPropinasPorEmpleado,
  TicketSeccionRendimientoEmpleado,
  TicketSeccionCuadranteNeto,
  TicketSeccionAuditoriaFlujo,
  TicketSeccionConsolidadoTurno,
  TicketSeccionGastosExternosDia,
  TicketTablaVentasPorMetodo,
  type Tabla2ColFila,
} from "./CierreTicketWebTablas";
import { formatTicketMoney } from "../utils/ticketCierreFormat";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { EmpleadoNombre } from "../utils/getEmpleadoNombre";
import type {
  CierreCaja,
  ConteoEfectivo,
  Credito,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import type { CierreTicketModel } from "../utils/buildCierreTicketModel";
import { formatMotivo } from "../utils/cierreHelpers";
import {
  formatCierreDateShortAmPm,
  formatCierreHourAmPm,
} from "../utils/cierreCuadranteHelper";
import { fmtEmp } from "../utils/getEmpleadoNombre";
import {
  buildVendedorNombrePorIdFromVentas,
  nombreResponsableMovimientoCaja,
} from "../utils/cajaResponsableMovimiento";
import {
  clienteNombreAbonoCredito,
  isMovimientoAbonoCredito,
  metodoPagoAbonoCorto,
} from "../utils/movimientoAbonoCredito";
import { CUADRE_TRANSFERENCIA_MOTIVOS } from "../constants/cuadreTransferenciaMotivos";
import { type MontosPorMetodoPago } from "../utils/propinaAllocation";
import {
  buildVentasPromocionesResumen,
  type CierrePromocionesResumen,
} from "../utils/ventaPromocionesResumen";
import {
  TICKET_CIERRE_TABLA_BLOQUE_DETALLE_COMPACTA,
  TICKET_CIERRE_TABLA_COL_SEP,
  TICKET_CIERRE_TABLA_COLUMNAS_ABONOS_CREDITOS,
  TICKET_CIERRE_TABLA_COLUMNAS_CREDITOS_SESION,
  TICKET_CIERRE_TABLA_COLUMNAS_METODO_INGRESOS,
  TICKET_CIERRE_TABLA_COLUMNAS_MOV_OTROS_CAJA,
  TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA,
  TICKET_CIERRE_TABLA_DATA_METODO_DER,
  TICKET_CIERRE_TABLA_DATA_METODO_IZQ,
  TICKET_CIERRE_TABLA_GRID,
  TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA,
  TICKET_CIERRE_TABLA_HEAD_METODO_DER,
  TICKET_CIERRE_TABLA_HEAD_METODO_IZQ,
  TICKET_CIERRE_TABLA_HR_FILA,
  TICKET_CIERRE_TABLA_MONO_BASE,
} from "../constants/ticketCierreTablaRejilla";

function loc(n: number): string {
  return formatTicketMoney(n);
}

const TICKET_DIVIDER_HR_CLASSES =
  "ticket-divider my-2.5 sm:my-4 h-0 shrink-0 border-0";

type MetodoPagoTotals = MontosPorMetodoPago & {
  transferencia: number;
  otros: number;
};

const ZERO_METODOS: MetodoPagoTotals = {
  efectivo: 0,
  nequi: 0,
  daviplata: 0,
  tarjeta: 0,
  transferencia: 0,
  otros: 0,
};

interface Props {
  cierre: CierreCaja;
  ventas: Venta[];
  model: CierreTicketModel;
  creditosSesion: Credito[];
  empleadosCatalog?: EmpleadoNombre[];
  isParcial: boolean;
}

function TicketDivider() {
  return <hr className={TICKET_DIVIDER_HR_CLASSES} />;
}

function TicketDashedLeaderRow({
  label,
  value,
  compact = false,
  strong = false,
  labelClassName = "",
  valueClassName = "",
}: {
  label: string;
  value: string;
  compact?: boolean;
  strong?: boolean;
  labelClassName?: string;
  valueClassName?: string;
}) {
  const sizeCls = compact
    ? "text-[7px] sm:text-[8px]"
    : "text-[9px] sm:text-[11px]";

  const labelCls = [
    "min-w-0 flex-1 break-words text-left leading-tight pr-1",
    !strong && compact && !labelClassName ? "font-bold" : "",
    labelClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const valueCls = [
    "shrink-0 self-start tabular-nums text-right whitespace-nowrap leading-tight",
    strong ? "" : compact ? "font-black" : "font-bold",
    valueClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`flex w-full min-w-0 justify-between items-start gap-2 sm:gap-3 py-1 sm:py-1.5 leading-tight text-gray-900 ${sizeCls} ${
        strong ? "font-black" : ""
      }`}
    >
      <span className={labelCls}>{label}</span>
      <span className={valueCls}>{value}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="border border-b-0 border-solid border-gray-800 px-2 py-1.5 sm:px-2.5 sm:py-2 text-[9px] sm:text-xs font-bold uppercase text-center">
      {children}
    </p>
  );
}

function SectionWrap({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`pt-3 pb-3 sm:pt-5 sm:pb-4 space-y-1.5 sm:space-y-2.5 [&>p:first-child+*]:!mt-0 ${className}`}
    >
      {children}
    </div>
  );
}

function horaTicketHHmm(iso: string): string {
  return formatCierreHourAmPm(iso);
}

function tipoMovLabel(t: MovimientoCaja["tipo"]): string {
  const map: Record<MovimientoCaja["tipo"], string> = {
    retiro: "RETIRO",
    gasto: "GASTO",
    ingreso: "INGRESO",
    devolucion: "DEVOLUCION",
    anulacion: "ANULACION",
    abrir_caja: "ABRIR CAJA",
  };
  return map[t] ?? String(t).toUpperCase();
}

function soporteMovimientoLine(m: MovimientoCaja): string | null {
  if (m.tipo !== "retiro" && m.tipo !== "gasto") return null;
  if (m.tipoSoporte === "con_factura") return "Con factura";
  if (m.tipoSoporte === "no_verificado") return "Sin verificar (pendiente)";
  return "Sin soporte";
}

function metodoIngresoLabel(m: MovimientoCaja): "efectivo" | "digital" | null {
  if (m.tipo !== "ingreso" || !m.metodoPago) return null;
  return m.metodoPago === "efectivo" ? "efectivo" : "digital";
}

function metodoPagoEtiqueta(mp: string): string {
  return getDefaultPaymentMethodLabel(mp);
}

function sumMontosByMetodoPagoIngresos(
  movs: MovimientoCaja[],
): MetodoPagoTotals {
  const acc: MetodoPagoTotals = { ...ZERO_METODOS };
  for (const m of movs) {
    if (m.tipo !== "ingreso") continue;
    const mp = m.metodoPago;
    if (!mp) {
      acc.otros += m.monto;
      continue;
    }
    if (mp in acc) acc[mp as keyof MetodoPagoTotals] += m.monto;
    else acc.otros += m.monto;
  }
  return acc;
}

function variantesCreditoLinea(p: Credito["productos"][number]): string {
  if (!p.variantesSeleccionadas?.length) return "";
  return p.variantesSeleccionadas
    .map((v) => `${v.groupLabel}: ${v.optionLabel}`)
    .join(" · ");
}

function cantidadLineaCredito(p: Credito["productos"][number]): string {
  if (p.peso != null && p.peso > 0 && p.precioPorKilo) {
    return `${p.peso} kg`;
  }
  return String(p.cantidad ?? 0);
}

function creditosSesionTieneDetalle(f: Credito): boolean {
  return Boolean(f.nota?.trim() || (f.productos?.length ?? 0) > 0);
}

function DesgloseEfectivoOficial({ conteo }: { conteo: ConteoEfectivo }) {
  return (
    <SectionWrap>
      <SectionTitle>Arqueo oficial billete a billete</SectionTitle>
      <TicketTablaBilletesMonedas conteo={conteo} totalLabel="Total contado" />
    </SectionWrap>
  );
}

function motivoAuditoriaDigital(motivo?: string): string {
  return (
    CUADRE_TRANSFERENCIA_MOTIVOS.find((option) => option.value === motivo)
      ?.label ??
    motivo ??
    "Sin motivo registrado"
  );
}

function TicketSeccionPromociones({
  resumen,
}: {
  resumen: CierrePromocionesResumen;
}) {
  const grupos: Tabla2ColFila[][] = [];
  // Límite por lista para no convertir el ticket en una fila de información interminable.
  const MAX_LIST = 8;

  if (resumen.totalDescuento > 0) {
    const filas: Tabla2ColFila[] = [
      {
        label: `Descuentos aplicados (${resumen.ventasConDescuento} ${resumen.ventasConDescuento === 1 ? "venta" : "ventas"})`,
        value: `−${loc(resumen.totalDescuento)}`,
        strong: true,
      },
    ];
    if (resumen.descuentoGlobal > 0)
      filas.push({
        label: "  Descuento global",
        value: `−${loc(resumen.descuentoGlobal)}`,
      });
    if (resumen.descuentoItems > 0)
      filas.push({
        label: "  Descuento por ítem",
        value: `−${loc(resumen.descuentoItems)}`,
      });
    if (resumen.descuentoIncentivo > 0)
      filas.push({
        label: "  Incentivo método de pago",
        value: `−${loc(resumen.descuentoIncentivo)}`,
      });
    grupos.push(filas);
  }

  if (resumen.lineasMayorista > 0 || resumen.lineasVip > 0) {
    const filas: Tabla2ColFila[] = [];
    if (resumen.lineasMayorista > 0)
      filas.push({
        label: `Líneas mayoristas (${resumen.lineasMayorista})`,
        value: loc(resumen.totalMayorista),
      });
    if (resumen.lineasVip > 0)
      filas.push({
        label: `Líneas VIP (${resumen.lineasVip})`,
        value: loc(resumen.totalVip),
      });
    grupos.push(filas);
  }

  if (resumen.puntosGanadosTurno > 0 || resumen.puntosCirculacion > 0) {
    const filas: Tabla2ColFila[] = [];
    if (resumen.puntosGanadosTurno > 0) {
      filas.push({
        label: "Puntos ganados en el turno",
        value: `+${resumen.puntosGanadosTurno.toLocaleString("es-CO")}`,
        strong: true,
      });
      const visibles = resumen.porCliente.slice(0, MAX_LIST);
      const ocultos = resumen.porCliente.length - visibles.length;
      filas.push(
        ...visibles.map((c) => ({
          label: `  ${c.nombre}${c.codigo ? ` (${c.codigo})` : ""}`,
          value: `${c.puntosGanados > 0 ? "+" : ""}${c.puntosGanados.toLocaleString(
            "es-CO",
          )}${
            c.saldoNuevo != null
              ? ` → ${c.saldoNuevo.toLocaleString("es-CO")} pts`
              : " pts"
          }`,
        })),
        ...(ocultos > 0
          ? [
              {
                label: `  … y ${ocultos} cliente${ocultos === 1 ? "" : "s"} más`,
                value: "",
              },
            ]
          : []),
      );
    }
    if (resumen.puntosCirculacion > 0) {
      filas.push({
        label: "Puntos en circulación (al cierre)",
        value: `${resumen.puntosCirculacion.toLocaleString("es-CO")} pts`,
        strong: true,
      });
    }
    grupos.push(filas);
  }

  if (resumen.premiosCanjeados.length > 0) {
    const visibles = resumen.premiosCanjeados.slice(0, MAX_LIST);
    const ocultos = resumen.premiosCanjeados.length - visibles.length;
    const filas: Tabla2ColFila[] = [
      {
        label: `Premios canjeados (${resumen.premiosCanjeados.length})`,
        value: `−${resumen.puntosCanjeadosTurno.toLocaleString("es-CO")} pts`,
        strong: true,
      },
      ...visibles.map((p) => ({
        label: `  ${formatCierreHourAmPm(p.hora ?? "")} · ${
          p.cliente ? `${p.cliente}: ` : ""
        }${p.nombre}`,
        value: `${p.puntos.toLocaleString("es-CO")} pts`,
      })),
      ...(ocultos > 0
        ? [
            {
              label: `  … y ${ocultos} premio${ocultos === 1 ? "" : "s"} más`,
              value: "",
            },
          ]
        : []),
    ];
    grupos.push(filas);
  }

  if (grupos.length === 0) return null;

  return (
    <SectionWrap>
      <SectionTitle>Descuentos, precios especiales y fidelización</SectionTitle>
      <TicketTabla2ColGrupos
        colIzqHeader="Concepto"
        colDerHeader="Valor"
        grupos={grupos}
        closeBottom={false}
      />
    </SectionWrap>
  );
}

function ResumenAuditoriaDigital({ cierre }: { cierre: CierreCaja }) {
  const verificacion = cierre.transferenciasVerificacion;
  if (!verificacion) return null;
  const auditorias = Object.entries(verificacion.porMetodo).filter(
    (entry): entry is [string, NonNullable<(typeof entry)[1]>] =>
      entry[1] != null,
  );
  const totales = auditorias.reduce(
    (total, [, audit]) => ({
      sistema: total.sistema + audit.esperado,
      declarado: total.declarado + (audit.declarado ?? 0),
      confirmados:
        total.confirmados +
        (audit.comprobantesConfirmados ?? audit.itemsVerificados?.length ?? 0),
      registrados: total.registrados + audit.cantidadEsperada,
      diferencias: total.diferencias + (audit.cuadrado ? 0 : 1),
    }),
    {
      sistema: 0,
      declarado: 0,
      confirmados: 0,
      registrados: 0,
      diferencias: 0,
    },
  );
  const summaryCell =
    "px-0.5 py-1 text-[6px] sm:text-[8px] leading-tight break-words min-w-0";

  return (
    <SectionWrap className="space-y-2 sm:space-y-3">
      <SectionTitle>Resumen Auditoría Digital</SectionTitle>
      <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
        <div
          className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
          style={{
            gridTemplateColumns:
              "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(2.4rem,.65fr) minmax(2.7rem,.7fr)",
          }}
        >
          {["Método", "Sistema", "Declarado", "Comp.", "Estado"].map(
            (label, index) => (
              <div
                key={label}
                className={`${summaryCell} font-black text-center ${index < 4 ? TICKET_CIERRE_TABLA_COL_SEP : ""}`}
              >
                {label}
              </div>
            ),
          )}
          <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
          {auditorias.map(([metodo, audit]) => {
            const confirmados =
              audit.comprobantesConfirmados ??
              audit.itemsVerificados?.length ??
              0;
            const values = [
              audit.label || metodo,
              loc(audit.esperado),
              loc(audit.declarado ?? 0),
              `${confirmados}/${audit.cantidadEsperada}`,
              audit.cuadrado ? "Exacto" : "Dif.",
            ];
            return values.map((value, index) => (
              <div
                key={`${metodo}-${index}`}
                className={`${summaryCell} ${index === 0 ? "font-bold" : "text-right tabular-nums"} ${index < 4 ? TICKET_CIERRE_TABLA_COL_SEP : ""}`}
              >
                {value}
              </div>
            ));
          })}
          <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
          {[
            "TOTAL",
            loc(totales.sistema),
            loc(totales.declarado),
            `${totales.confirmados}/${totales.registrados}`,
            totales.diferencias > 0 ? `${totales.diferencias} Dif.` : "Exacto",
          ].map((value, index) => (
            <div
              key={`total-${index}`}
              className={`${summaryCell} font-black ${index === 0 ? "" : "text-right tabular-nums"} ${index < 4 ? TICKET_CIERRE_TABLA_COL_SEP : ""}`}
            >
              {value}
            </div>
          ))}
        </div>
      </div>

      {auditorias.map(([metodo, audit]) => {
        const confirmados =
          audit.comprobantesConfirmados ?? audit.itemsVerificados?.length ?? 0;
        const intentos = audit.intentos ?? [];
        const problemas = audit.itemsConProblema ?? [];
        const estado = audit.cuadrado
          ? "CUADRE EXACTO"
          : audit.intentosAgotados
            ? "DIFERENCIA FINAL"
            : "AUDITORÍA PENDIENTE";
        const diferenciaLabel = `${audit.diferencia >= 0 ? "+" : "−"}${loc(Math.abs(audit.diferencia))}`;
        const resultadoConciliacion = audit.cuadrado
          ? "CONCILIACIÓN COMPLETA"
          : "DECLARACIÓN DIFERENTE";

        return (
          <div key={metodo} className="space-y-2 sm:space-y-3">
            <div className="space-y-0">
              <SectionTitle>{audit.label || metodo}</SectionTitle>
              <TicketTabla2ColGrupos
                colIzqHeader="Concepto"
                colDerHeader="Valor"
                grupos={[
                  [
                    {
                      label: "Total registrado por sistema",
                      value: loc(audit.esperado),
                    },
                    {
                      label: "Total declarado",
                      value: loc(audit.declarado ?? 0),
                    },
                    { label: "Diferencia final", value: diferenciaLabel },
                    {
                      label: "Intentos utilizados",
                      value: `${intentos.length} / 3`,
                    },
                    {
                      label: "Comprobantes registrados",
                      value: String(audit.cantidadEsperada),
                    },
                    {
                      label: "Comprobantes confirmados",
                      value: `${confirmados} / ${audit.cantidadEsperada}`,
                    },
                    {
                      label: "Valor auditado (comprobantes)",
                      value: loc(audit.sumaComprobantesMarcados ?? 0),
                    },
                    ...(audit.sumaComprobantesConProblema
                      ? [
                          {
                            label: "Valor no confirmado",
                            value: loc(audit.sumaComprobantesConProblema),
                          },
                        ]
                      : []),
                    { label: "Estado", value: estado, strong: true },
                  ],
                ]}
                closeBottom={false}
              />
            </div>

            {audit.totalTarifas ? (
              <div className="space-y-0">
                <SectionTitle>Resumen pasarela de pago</SectionTitle>
                <TicketTabla2ColGrupos
                  colIzqHeader="Concepto"
                  colDerHeader="Valor"
                  showHeader={false}
                  grupos={[
                    [
                      { label: "Método", value: audit.label || metodo },
                      {
                        label: "Comprobantes registrados",
                        value: String(audit.cantidadEsperada),
                      },
                      {
                        label: "Total cobrado al cliente",
                        value: loc(audit.totalCobrado ?? audit.esperado),
                      },
                      {
                        label: "Comisión total descontada",
                        value: `−${loc(audit.totalTarifas)}`,
                      },
                      {
                        label: "Neto esperado en cuenta",
                        value: loc(audit.esperado),
                        strong: true,
                      },
                    ],
                  ]}
                  closeBottom={false}
                />
              </div>
            ) : null}

            {intentos.length > 0 ? (
              <div className="space-y-0">
                <SectionTitle>Historial de verificaciones</SectionTitle>
                <TicketTabla2ColGrupos
                  colIzqHeader="Intento"
                  colDerHeader="Declarado / Diferencia"
                  grupos={[
                    intentos.map((intento) => ({
                      label: `${intento.numero} · ${formatCierreHourAmPm(intento.registradoEn)}`,
                      value: `${loc(intento.declarado)} / ${intento.diferencia >= 0 ? "+" : "−"}${loc(Math.abs(intento.diferencia))}`,
                    })),
                  ]}
                  closeBottom={false}
                />
              </div>
            ) : null}

            <div className="space-y-0">
              <SectionTitle>Conciliación de valores</SectionTitle>
              <TicketTabla2ColGrupos
                colIzqHeader="Concepto"
                colDerHeader="Valor"
                showHeader={false}
                grupos={[
                  [
                    { label: "Sistema", value: loc(audit.esperado) },
                    { label: "Declarado", value: loc(audit.declarado ?? 0) },
                    {
                      label: "Comprobantes auditados",
                      value: loc(audit.sumaComprobantesMarcados ?? 0),
                    },
                  ],
                  [
                    {
                      label: "Resultado",
                      value: resultadoConciliacion,
                      strong: true,
                    },
                    ...(!audit.cuadrado
                      ? [
                          {
                            label: "Diferencia final",
                            value: diferenciaLabel,
                            strong: true,
                          },
                        ]
                      : []),
                  ],
                ]}
                closeBottom={false}
              />
            </div>

            <div className="space-y-0">
              <SectionTitle>Comprobantes no confirmados</SectionTitle>
              <TicketTabla2ColGrupos
                colIzqHeader="Comprobante"
                colDerHeader="Detalle"
                grupos={[
                  problemas.map((p) => ({
                    label: `${formatCierreHourAmPm(p.fecha)} · ${loc(p.monto)}`,
                    value: `${motivoAuditoriaDigital(p.motivo)}${
                      p.detalle ? ` — ${p.detalle}` : ""
                    }`,
                  })),
                ]}
                closeBottom={false}
              />
            </div>
          </div>
        );
      })}
      <TicketDashedLeaderRow
        label="Declaración de soportes"
        value={verificacion.declaracionAceptada ? "Aceptada" : "Pendiente"}
      />
    </SectionWrap>
  );
}

function TablaCreditosSesion({ creditos }: { creditos: Credito[] }) {
  const headCell = TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA;
  const dataCell = TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA;

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={TICKET_CIERRE_TABLA_GRID}
        style={TICKET_CIERRE_TABLA_COLUMNAS_CREDITOS_SESION}
      >
        <div
          className={`${headCell} break-words ${TICKET_CIERRE_TABLA_COL_SEP}`}
        >
          Cliente
        </div>
        <div
          className={`${headCell} tabular-nums whitespace-nowrap ${TICKET_CIERRE_TABLA_COL_SEP}`}
        >
          Total
        </div>
        <div
          className={`${headCell} tabular-nums whitespace-nowrap ${TICKET_CIERRE_TABLA_COL_SEP}`}
        >
          Fecha
        </div>
        <div className={`${headCell} break-words`}>Registró</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {creditos.map((f, idx) => {
          const detalle = creditosSesionTieneDetalle(f);
          return (
            <Fragment key={f.id}>
              <div
                className={`${dataCell} break-words font-bold text-gray-900 ${TICKET_CIERRE_TABLA_COL_SEP}`}
              >
                {f.clienteNombre || "Cliente"}
              </div>
              <div
                className={`${dataCell} tabular-nums font-black whitespace-nowrap ${TICKET_CIERRE_TABLA_COL_SEP}`}
              >
                {loc(f.total)}
              </div>
              <div
                className={`${dataCell} tabular-nums text-gray-700 whitespace-nowrap ${TICKET_CIERRE_TABLA_COL_SEP}`}
              >
                {format(new Date(f.fecha), "dd/MM HH:mm", { locale: es })}
              </div>
              <div className={`${dataCell} break-words text-gray-800`}>
                {fmtEmp(f.userName || f.usuarioId)}
              </div>
              {detalle ? (
                <div className={TICKET_CIERRE_TABLA_BLOQUE_DETALLE_COMPACTA}>
                  {(f.productos?.length ?? 0) > 0 ? (
                    <ul className="m-0 list-none space-y-1 p-0 text-[7px] sm:text-[8px] text-gray-800 w-full min-w-0">
                      {(f.productos ?? []).map((p, pidx) => {
                        const vars = variantesCreditoLinea(p);
                        return (
                          <li
                            key={`${f.id}-p-${pidx}-${p.productoId}`}
                            className="break-words min-w-0 leading-tight m-0"
                          >
                            <span className="font-black">•</span> {p.nombre}
                            {vars ? (
                              <span className="text-gray-600"> — {vars}</span>
                            ) : null}
                            <span className="text-gray-600">
                              {" "}
                              × {cantidadLineaCredito(p)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {f.nota?.trim() ? (
                    <p className="text-[7px] sm:text-[8px] text-gray-700 break-words min-w-0 leading-snug m-0">
                      <span className="font-bold text-gray-800">Nota:</span>{" "}
                      {f.nota.trim()}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {idx < creditos.length - 1 ? (
                <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function AbonosCreditosTablaCompacta({
  movs,
  ventas,
  empleadosCatalog,
}: {
  movs: MovimientoCaja[];
  ventas: Venta[];
  empleadosCatalog?: EmpleadoNombre[];
}) {
  const head = TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA;
  const cell = TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA;
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const nombrePorUsuarioId = useMemo(
    () => buildVendedorNombrePorIdFromVentas(ventas),
    [ventas],
  );

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={TICKET_CIERRE_TABLA_GRID}
        style={TICKET_CIERRE_TABLA_COLUMNAS_ABONOS_CREDITOS}
      >
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Hora
        </div>
        <div className={`${head} break-words min-w-0 ${sep}`}>Cliente</div>
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Ingreso
        </div>
        <div className={`${head} whitespace-nowrap ${sep}`}>Mét.</div>
        <div className={`${head} break-words min-w-0`}>Resp.</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {movs.map((m, idx) => {
          const cliente = clienteNombreAbonoCredito(m.motivo || "");
          const resp = nombreResponsableMovimientoCaja(m, {
            empleadosCatalog,
            nombrePorUsuarioIdFromVentas: nombrePorUsuarioId,
          });
          const hora = horaTicketHHmm(m.fecha);
          return (
            <Fragment key={m.id}>
              <div
                className={`${cell} tabular-nums text-gray-800 whitespace-nowrap ${sep}`}
                title={hora}
              >
                {hora}
              </div>
              <div
                className={`${cell} break-words min-w-0 font-medium text-gray-900 ${sep}`}
                title={cliente}
              >
                {cliente}
              </div>
              <div
                className={`${cell} tabular-nums font-black text-gray-900 whitespace-nowrap ${sep}`}
                title={`+${loc(m.monto)}`}
              >
                +{loc(m.monto)}
              </div>
              <div
                className={`${cell} text-gray-800 whitespace-nowrap ${sep}`}
                title={m.metodoPago ?? ""}
              >
                {metodoPagoAbonoCorto(m.metodoPago)}
              </div>
              <div
                className={`${cell} break-words min-w-0 text-gray-800`}
                title={resp}
              >
                {resp}
              </div>
              {idx < movs.length - 1 ? (
                <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function IngresosPorMetodoTabla({
  totales,
  totalGeneral,
  titulo = "Por método",
  subtotalLabel = "Subtotal",
}: {
  totales: MetodoPagoTotals;
  totalGeneral: number;
  titulo?: string;
  subtotalLabel?: string;
}) {
  const keys = [
    "efectivo",
    "nequi",
    "daviplata",
    "tarjeta",
    "transferencia",
  ] as const;
  const filas = keys.filter((k) => totales[k] > 0);
  const hayOtros = totales.otros > 0;
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const totalDigital =
    totales.nequi +
    totales.daviplata +
    totales.tarjeta +
    totales.transferencia +
    totales.otros;

  return (
    <div
      className={
        titulo
          ? "pt-2 mt-1 border-t border-gray-400 text-gray-900"
          : "text-gray-900"
      }
    >
      {titulo ? (
        <p className="font-bold uppercase text-gray-600 mb-0.5 text-[7px] sm:text-[8px] font-mono">
          {titulo}
        </p>
      ) : null}
      <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
        <div
          className={TICKET_CIERRE_TABLA_GRID}
          style={TICKET_CIERRE_TABLA_COLUMNAS_METODO_INGRESOS}
        >
          <div className={`${TICKET_CIERRE_TABLA_HEAD_METODO_IZQ} ${sep}`}>
            Método
          </div>
          <div className={TICKET_CIERRE_TABLA_HEAD_METODO_DER}>Ingreso</div>
          <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

          {filas.map((k) => (
            <Fragment key={k}>
              <div
                className={`${TICKET_CIERRE_TABLA_DATA_METODO_IZQ} font-medium ${sep}`}
              >
                {metodoPagoEtiqueta(k)}
              </div>
              <div className={TICKET_CIERRE_TABLA_DATA_METODO_DER}>
                {loc(totales[k])}
              </div>
              <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
            </Fragment>
          ))}
          {hayOtros ? (
            <Fragment>
              <div
                className={`${TICKET_CIERRE_TABLA_DATA_METODO_IZQ} font-medium ${sep}`}
              >
                Otros
              </div>
              <div className={TICKET_CIERRE_TABLA_DATA_METODO_DER}>
                {loc(totales.otros)}
              </div>
              <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
            </Fragment>
          ) : null}

          <div className="col-start-1 [grid-column:1/-1] w-full min-w-0 space-y-0.5 pt-0.5">
            {totales.efectivo > 0 ? (
              <TicketDashedLeaderRow
                compact
                label="Total efectivo"
                value={loc(totales.efectivo)}
                labelClassName="font-bold"
              />
            ) : null}
            {totalDigital > 0 ? (
              <TicketDashedLeaderRow
                compact
                label="Total digital"
                value={loc(totalDigital)}
                labelClassName="font-bold"
              />
            ) : null}
            <TicketDashedLeaderRow
              compact
              label={subtotalLabel}
              value={`+${loc(totalGeneral)}`}
              labelClassName="font-black uppercase"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TablaOtrosMovimientosCaja({
  movs,
  ventas,
  empleadosCatalog,
}: {
  movs: MovimientoCaja[];
  ventas: Venta[];
  empleadosCatalog?: EmpleadoNombre[];
}) {
  const head = TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA;
  const cell = TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA;
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const col = TICKET_CIERRE_TABLA_COLUMNAS_MOV_OTROS_CAJA;
  const nombrePorUsuarioId = useMemo(
    () => buildVendedorNombrePorIdFromVentas(ventas),
    [ventas],
  );

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div className={TICKET_CIERRE_TABLA_GRID} style={col}>
        <div className={`${head} break-words min-w-0 ${sep}`}>Tipo</div>
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Monto
        </div>
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Hora
        </div>
        <div className={`${head} break-words min-w-0`}>Resp.</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {movs.map((m, idx) => {
          const ingreso = m.tipo === "ingreso";
          const esApertura = m.tipo === "abrir_caja";
          const sop = soporteMovimientoLine(m);
          const medio = metodoIngresoLabel(m);
          const hora = horaTicketHHmm(m.fecha);
          const resp = nombreResponsableMovimientoCaja(m, {
            empleadosCatalog,
            nombrePorUsuarioIdFromVentas: nombrePorUsuarioId,
          });
          const tipo = tipoMovLabel(m.tipo);
          return (
            <Fragment key={m.id}>
              <div
                className={`${cell} break-words min-w-0 font-medium text-gray-900 ${sep}`}
                title={tipo}
              >
                {tipo}
              </div>
              <div
                className={`${cell} tabular-nums font-black text-gray-900 whitespace-nowrap ${sep}`}
              >
                {esApertura
                  ? "GAVETA"
                  : `${ingreso ? "+" : "−"}${loc(m.monto)}`}
              </div>
              <div
                className={`${cell} tabular-nums text-gray-800 whitespace-nowrap ${sep}`}
                title={hora}
              >
                {hora}
              </div>
              <div
                className={`${cell} break-words min-w-0 text-gray-800`}
                title={resp}
              >
                {resp}
              </div>
              <div className={TICKET_CIERRE_TABLA_BLOQUE_DETALLE_COMPACTA}>
                <p className="m-0 text-[7px] sm:text-[8px] text-gray-800 break-words min-w-0 leading-snug">
                  {m.motivo}
                </p>
                {sop ? (
                  <p className="m-0 text-[7px] sm:text-[8px] text-gray-700 leading-snug">
                    <span className="font-bold text-gray-800">Soporte:</span>{" "}
                    {sop}
                  </p>
                ) : null}
                {medio ? (
                  <p className="m-0 text-[7px] sm:text-[8px] text-gray-700 leading-snug">
                    {`Medio: ${medio === "efectivo" ? "Efectivo" : "Digital"} (${m.metodoPago})`}
                  </p>
                ) : null}
              </div>
              {idx < movs.length - 1 ? (
                <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function ResumenMovimientosCaja({ movs }: { movs: MovimientoCaja[] }) {
  const filas = (
    [
      "ingreso",
      "retiro",
      "gasto",
      "devolucion",
      "anulacion",
      "abrir_caja",
    ] as const
  )
    .map((tipo) => {
      const movimientos = movs.filter((movimiento) => movimiento.tipo === tipo);
      return {
        tipo,
        cantidad: movimientos.length,
        valor: movimientos.reduce(
          (total, movimiento) => total + movimiento.monto,
          0,
        ),
      };
    })
    .filter((fila) => fila.cantidad > 0);

  const etiquetas: Record<MovimientoCaja["tipo"], string> = {
    ingreso: "Ingresos",
    retiro: "Retiros",
    gasto: "Gastos",
    devolucion: "Devoluciones",
    anulacion: "Anulaciones",
    abrir_caja: "Abrir caja",
  };

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
        style={{
          gridTemplateColumns:
            "minmax(0,1fr) minmax(2.75rem,auto) minmax(4.5rem,auto)",
        }}
      >
        <div
          className={`${TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA} ${TICKET_CIERRE_TABLA_COL_SEP}`}
        >
          Concepto
        </div>
        <div
          className={`${TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA} ${TICKET_CIERRE_TABLA_COL_SEP}`}
        >
          Cant.
        </div>
        <div className={TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA}>Valor</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
        {filas.map((fila) => (
          <Fragment key={fila.tipo}>
            <div
              className={`${TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA} font-medium ${TICKET_CIERRE_TABLA_COL_SEP}`}
            >
              {etiquetas[fila.tipo]}
            </div>
            <div
              className={`${TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA} text-center tabular-nums ${TICKET_CIERRE_TABLA_COL_SEP}`}
            >
              {fila.cantidad}
            </div>
            <div
              className={`${TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA} text-right font-black tabular-nums`}
            >
              {fila.tipo === "abrir_caja"
                ? "GAVETA"
                : `${fila.tipo === "ingreso" ? "+" : "−"}${loc(fila.valor)}`}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function MonitorRiesgoArqueoCiego({
  cierre,
  contadoOficial,
  esperadoGaveta,
}: {
  cierre: CierreCaja;
  contadoOficial: number;
  esperadoGaveta: number;
}) {
  const audit = cierre.cierreCiegoAudit;
  const intentosFallidos = audit?.intentosFallidos ?? 0;
  const intentos = audit?.intentos ?? [];
  const ultimo =
    intentos[intentos.length - 1] ??
    (audit && audit.montosDeclarados.length > 0
      ? {
          numero: audit.montosDeclarados.length,
          total: audit.montosDeclarados[audit.montosDeclarados.length - 1],
          registradoEn: cierre.cerradaEn ?? cierre.abiertaEn,
        }
      : null);

  const ultimoConteo =
    audit?.ultimoIntentoFallidoConteo ?? ultimo?.conteoEfectivo;
  const faltanteOficial = contadoOficial - esperadoGaveta;
  const estadoFinal = audit?.desbloqueadoPorLimite
    ? "Desbloqueado por límite de intentos"
    : intentosFallidos > 0
      ? "Cuadrado después de reintento"
      : "Sin intentos fallidos registrados";

  return (
    <SectionWrap>
      <SectionTitle>Monitor de riesgo — arqueo a ciegas</SectionTitle>
      <TicketDashedLeaderRow
        label="Intentos fallidos registrados:"
        value={`${intentosFallidos}/3`}
        strong
      />
      {intentosFallidos > 0 ? (
        <TicketTablaIntentosArqueoCiego
          intentos={intentos}
          montosFallback={audit?.montosDeclarados ?? []}
          fallbackHoraIso={cierre.cerradaEn ?? cierre.abiertaEn}
        />
      ) : null}
      {ultimoConteo ? (
        <>
          <p className="text-[8px] sm:text-[9px] font-bold uppercase text-gray-600 mt-2">
            Último intento fallido — detalle (Intento {ultimo?.numero ?? 3})
          </p>
          <TicketTablaBilletesMonedas
            conteo={ultimoConteo}
            totalLabel={`Total intento ${ultimo?.numero ?? 3} (fallido)`}
          />
        </>
      ) : null}
      <p className="text-[8px] sm:text-[9px] font-bold uppercase text-gray-600 mt-2">
        Comparación con arqueo oficial:
      </p>
      <TicketTablaComparacionArqueo
        filas={[
          {
            label: "Contado oficial (cierre)",
            value: loc(contadoOficial),
          },
          ...(ultimo
            ? [
                {
                  label: `Último intento fallido (${ultimo.numero})`,
                  value: loc(ultimo.total),
                },
                {
                  label: `Diferencia (oficial − intento ${ultimo.numero})`,
                  value: `${contadoOficial - ultimo.total >= 0 ? "+" : ""}${loc(contadoOficial - ultimo.total)}`,
                },
              ]
            : []),
          {
            label: "vs Esperado gaveta",
            value: loc(faltanteOficial),
            valueClassName:
              faltanteOficial === 0
                ? "text-green-700"
                : faltanteOficial > 0
                  ? "text-blue-700"
                  : "text-red-700",
          },
        ]}
      />
      <TicketTabla2ColGrupos
        colIzqHeader="Estado final"
        colDerHeader="Resultado"
        grupos={[
          [
            {
              label: "Estado",
              value: estadoFinal,
              strong: true,
            },
            {
              label: "Monto legal del cierre",
              value: loc(contadoOficial),
              strong: true,
            },
          ],
        ]}
        closeBottom={false}
      />
    </SectionWrap>
  );
}

export function CierreTicketWebBody({
  cierre,
  ventas,
  model,
  creditosSesion,
  empleadosCatalog,
  isParcial,
}: Props) {
  const { audit } = model;
  const diferencia = model.diferencia;

  const movCreditoAbonos = audit.movCreditoAbonos;
  const movOtros = audit.movOtros;
  const movSorted = useMemo(
    () =>
      [...audit.movimientosActivos].sort(
        (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
      ),
    [audit.movimientosActivos],
  );
  const movOtrosSorted = useMemo(
    () => movSorted.filter((m) => !isMovimientoAbonoCredito(m)),
    [movSorted],
  );

  const creditoTotalesPorMetodo = useMemo(
    () => sumMontosByMetodoPagoIngresos(movCreditoAbonos),
    [movCreditoAbonos],
  );
  const totalCreditoAbonos = useMemo(
    () => movCreditoAbonos.reduce((s, m) => s + m.monto, 0),
    [movCreditoAbonos],
  );

  const ingresosManuales = useMemo(
    () => movOtros.filter((m) => m.tipo === "ingreso"),
    [movOtros],
  );
  const ingresosManualesPorMetodo = useMemo(
    () => sumMontosByMetodoPagoIngresos(ingresosManuales),
    [ingresosManuales],
  );
  const totalIngresosManuales = useMemo(
    () => ingresosManuales.reduce((s, m) => s + m.monto, 0),
    [ingresosManuales],
  );

  const totalRetiros = audit.totalRetirosSeguridad + audit.retirosOtros;

  const promociones = useMemo(
    () => buildVentasPromocionesResumen(ventas),
    [ventas],
  );

  const ingresosCajaFisica = useMemo((): Tabla2ColFila[] => {
    const rows: Tabla2ColFila[] = [
      { label: "Base Inicial", value: loc(cierre.montoInicial) },
      {
        label: "+ Ventas Efectivo (Netas)",
        value: loc(model.ventasCobradas.efectivo),
      },
    ];
    if (audit.totalIngresosManualesEfectivo > 0) {
      rows.push({
        label: "+ INGRESO (Mov. de Caja)",
        value: loc(audit.totalIngresosManualesEfectivo),
      });
    }
    if (audit.totalAbonosCreditoEfectivo > 0) {
      rows.push({
        label: "+ ABONOS A CRÉDITO (efectivo)",
        value: loc(audit.totalAbonosCreditoEfectivo),
      });
    }
    if (audit.propinasEfectivo > 0) {
      rows.push({
        label: "+ PROPINAS (Servicio · efectivo)",
        value: loc(audit.propinasEfectivo),
      });
    }
    return rows;
  }, [audit, cierre.montoInicial, model.ventasCobradas.efectivo]);

  const egresosCajaFisica = useMemo((): Tabla2ColFila[] => {
    const rows: Tabla2ColFila[] = [];
    if (totalRetiros > 0) {
      rows.push({
        label: "− RETIRO (Mov. de Caja)",
        value: `−${loc(totalRetiros)}`,
      });
    }
    if (audit.totalGastos > 0) {
      rows.push({
        label: "− GASTOS (Mov. de Caja)",
        value: `−${loc(audit.totalGastos)}`,
      });
    }
    if (audit.totalDevolucionesEfectivo > 0) {
      rows.push({
        label: "− DEVOLUCIÓN (Mov. de Caja)",
        value: `−${loc(audit.totalDevolucionesEfectivo)}`,
      });
    }
    return rows;
  }, [audit, totalRetiros]);

  const { resumenOperativo } = model;
  const totalCobradoVentas = Object.values(model.ventasCobradas).reduce(
    (total, monto) => total + monto,
    0,
  );
  const showCiegoAudit = !isParcial;

  return (
    <>
      {/* Confirmación posterior de la base que se dejó para el siguiente turno. */}
      {!isParcial && cierre.baseAperturaConfirmacion ? (
        <>
          <SectionWrap>
            <SectionTitle>Confirmación de base siguiente apertura</SectionTitle>
            <TicketDashedLeaderRow
              label="Decisión:"
              value={cierre.baseAperturaConfirmacion.decisionBase}
            />
            <TicketDashedLeaderRow
              label="Base usada al abrir:"
              value={loc(cierre.baseAperturaConfirmacion.montoApertura)}
              strong
            />
            <TicketDashedLeaderRow
              label="Variación frente al cierre:"
              value={`${cierre.baseAperturaConfirmacion.variacionBase >= 0 ? "+" : "−"}${loc(Math.abs(cierre.baseAperturaConfirmacion.variacionBase))}`}
            />
            <TicketDashedLeaderRow
              label="Confirmada:"
              value={formatCierreDateShortAmPm(
                cierre.baseAperturaConfirmacion.confirmadaEn,
              )}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 1 — Resumen del turno y Top 5 */}
      {resumenOperativo.ventasActivasCount > 0 ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketResumenOperativoTurno
              resumen={resumenOperativo}
              totalCobradoVentas={totalCobradoVentas}
              totalEfectivo={model.ventasCobradas.efectivo}
              totalDigital={model.totalDigitalCobrado}
              recaudoTotal={model.totalRecaudado}
              propinaTotal={model.propinaTotal}
              movimientos={{
                ingresosManualesEfectivo: audit.totalIngresosManualesEfectivo,
                abonosCreditoEfectivo: audit.totalAbonosCreditoEfectivo,
                retiros: totalRetiros,
                gastos: audit.totalGastos,
                devolucionesEfectivo: audit.totalDevolucionesEfectivo,
              }}
              arqueo={{
                esperadoGaveta: model.esperadoGaveta,
                contadoReal: model.contadoReal,
                diferenciaArqueo: model.diferencia,
              }}
              baseApertura={cierre.aperturaBase ?? null}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {resumenOperativo.topProductos.length > 0 ? (
        <>
          <SectionWrap className="space-y-0 pt-1 sm:pt-2">
            <TicketSeccionTopProductos resumen={resumenOperativo} />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 1b — Descuentos, precios especiales y fidelización */}
      <TicketSeccionPromociones resumen={promociones} />
      {promociones.totalDescuento > 0 ||
      promociones.puntosGanadosTurno > 0 ||
      promociones.puntosCirculacion > 0 ||
      promociones.premiosCanjeados.length > 0 ||
      promociones.lineasMayorista > 0 ||
      promociones.lineasVip > 0 ? (
        <TicketDivider />
      ) : null}

      {/* 2 — Ventas por método (tabla Método de Pago | Total) */}
      <SectionWrap>
        <SectionTitle>Ingresos por método</SectionTitle>
        <TicketTablaVentasPorMetodo model={model} />
      </SectionWrap>
      <TicketDivider />

      {/* 3 — Caja física (tabla Concepto de Flujo | Valor) */}
      <SectionWrap>
        <SectionTitle>Movimiento del efectivo</SectionTitle>
        <TicketTablaCajaFisica
          ingresos={ingresosCajaFisica}
          egresos={egresosCajaFisica}
          esperado={model.esperadoGaveta}
        />
      </SectionWrap>
      <TicketDivider />

      {/* Arqueo oficial y monitor inmediatamente después del movimiento. */}
      {!isParcial && cierre.conteoEfectivo ? (
        <>
          <DesgloseEfectivoOficial conteo={cierre.conteoEfectivo} />
          <TicketDivider />
        </>
      ) : null}

      {showCiegoAudit ? (
        <>
          <MonitorRiesgoArqueoCiego
            cierre={cierre}
            contadoOficial={model.contadoReal}
            esperadoGaveta={model.esperadoGaveta}
          />
          <TicketDivider />
        </>
      ) : null}

      {!isParcial ? (
        <>
          <SectionWrap>
            <SectionTitle>Resultado del arqueo</SectionTitle>
            <TicketTablaComparacionArqueo
              filas={[
                {
                  label: "Efectivo esperado en gaveta",
                  value: loc(model.esperadoGaveta),
                },
                {
                  label: "Efectivo contado",
                  value: loc(model.contadoReal),
                },
                {
                  label:
                    diferencia < 0
                      ? "Faltante físico de caja"
                      : diferencia > 0
                        ? "Sobrante físico de caja"
                        : "Arqueo cuadrado",
                  value:
                    diferencia === 0
                      ? loc(0)
                      : `${diferencia > 0 ? "+" : "−"}${loc(Math.abs(diferencia))}`,
                  strong: true,
                  valueClassName:
                    diferencia >= 0 ? "text-emerald-700" : "text-red-700",
                },
              ]}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {!isParcial &&
      (diferencia !== 0 ||
        cierre.motivoDescuadre ||
        cierre.notaDescuadre ||
        cierre.extraNote) ? (
        <>
          <SectionWrap className="space-y-0.5 sm:space-y-1">
            <SectionTitle>Observación</SectionTitle>
            <div className="border border-t-0 border-solid border-gray-800 px-2 py-1.5 sm:px-2.5 sm:py-2 space-y-1">
              {cierre.motivoDescuadre ? (
                <p className="text-[9px] sm:text-[11px]">
                  Motivo:{" "}
                  <strong>{formatMotivo(cierre.motivoDescuadre)}</strong>
                </p>
              ) : null}
              {cierre.notaDescuadre ? (
                <p className="text-[9px] sm:text-[11px] italic whitespace-pre-line">
                  {cierre.notaDescuadre}
                </p>
              ) : null}
              {cierre.extraNote ? (
                <div className="space-y-0.5">
                  <p className="text-[9px] sm:text-[11px] font-bold">
                    Mensaje adicional:
                  </p>
                  <p className="text-[9px] sm:text-[11px] italic whitespace-pre-line">
                    {cierre.extraNote}
                  </p>
                </div>
              ) : null}
              <div className="text-center pt-2 space-y-0.5 border-t border-solid border-gray-300 mt-2">
                <p className="text-[8px] sm:text-[10px] font-bold">
                  Responsable: {cierre.closedByName || cierre.userId}
                </p>
                <p className="text-[7px] sm:text-[9px] text-gray-500">
                  ID: {cierre.id.slice(0, 14).toUpperCase()}
                </p>
              </div>
            </div>
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 12 — Ingresos manuales */}
      {totalIngresosManuales > 0 ? (
        <>
          <SectionWrap className="space-y-1.5 sm:space-y-2">
            <SectionTitle>Ingresos manuales por método</SectionTitle>
            <IngresosPorMetodoTabla
              totales={ingresosManualesPorMetodo}
              totalGeneral={totalIngresosManuales}
              titulo=""
              subtotalLabel="Subtotal ingresos manuales"
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 13 — Cuadrante neto (definitivo) */}
      {!isParcial ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionCuadranteNeto
              contadoReal={model.contadoReal}
              baseReservada={cierre.baseSiguiente ?? cierre.montoInicial}
              propinasEfectivo={audit.propinasEfectivo}
              efectivoNetoEntregar={model.efectivoNetoEntregar}
              decisionBaseRegistrada={typeof cierre.baseSiguiente === "number"}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 13b — Verificación de la base de apertura: cadena cierre anterior → apertura.
          Se ubica tras «Dinero a entregar» para que el flujo de efectivo se lea primero. */}
      {!isParcial && cierre.aperturaBase ? (
        <>
          <SectionWrap>
            <SectionTitle>
              Verificación de la base — Apertura del turno
            </SectionTitle>
            <TicketVerificacionBaseApertura apertura={cierre.aperturaBase} />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {!isParcial && typeof cierre.baseSiguiente === "number" ? (
        <>
          <SectionWrap>
            <SectionTitle>Base para el siguiente turno</SectionTitle>
            <div className="border border-t-0 border-solid border-gray-800 px-2 py-1.5 sm:px-2.5 sm:py-2">
              <TicketDashedLeaderRow
                label="Decisión:"
                value={
                  cierre.baseSiguiente > 0 ? "Se dejó base" : "No se dejó base"
                }
                strong
              />
              <TicketDashedLeaderRow
                label="Base reservada:"
                value={loc(cierre.baseSiguiente)}
              />
              <TicketDashedLeaderRow
                label="Efectivo entregado al dueño:"
                value={loc(model.efectivoNetoEntregar)}
                strong
              />
            </div>
            {cierre.baseSiguiente > 0 && cierre.baseSiguienteConteo ? (
              <div className="space-y-0 pt-2">
                <SectionTitle>
                  Composición física de la base reservada
                </SectionTitle>
                <TicketTablaBilletesMonedas
                  conteo={cierre.baseSiguienteConteo}
                  totalLabel="Total base reservada"
                />
              </div>
            ) : null}
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {!isParcial ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionAuditoriaFlujo
              audit={audit}
              totalRecaudado={model.totalRecaudado}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 15 — Consolidado del turno */}
      {!isParcial ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionConsolidadoTurno
              totalRecaudado={model.totalRecaudado}
              audit={audit}
              dineroEsperadoCierre={model.dineroEsperadoCierre}
              dineroEncontradoCierre={model.dineroEncontradoCierre}
              diferenciaFinancieraCierre={model.diferenciaFinancieraCierre}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 15b — Gastos del dueño (día, fuera de caja) */}
      {!isParcial && model.totalGastosExternosDia > 0 ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionGastosExternosDia
              gastos={model.gastosExternosDia}
              total={model.totalGastosExternosDia}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 16 — Movimientos de caja (sin abonos credito) */}
      {movOtrosSorted.length > 0 ? (
        <>
          <SectionWrap className="space-y-0">
            <SectionTitle>Movimientos ({movOtrosSorted.length})</SectionTitle>
            <ResumenMovimientosCaja movs={movOtrosSorted} />
          </SectionWrap>
          <TicketDivider />
          <SectionWrap className="space-y-1.5 sm:space-y-2">
            <SectionTitle>
              Movimientos de caja ({movOtrosSorted.length})
            </SectionTitle>
            <TablaOtrosMovimientosCaja
              movs={movOtrosSorted}
              ventas={ventas}
              empleadosCatalog={empleadosCatalog}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 12 — Creditos con su detalle inmediatamente debajo de cada cliente */}
      {creditosSesion.length > 0 ? (
        <>
          <SectionWrap className="space-y-1.5 sm:space-y-2">
            <SectionTitle>
              Creditos otorgados en el turno ({creditosSesion.length})
            </SectionTitle>
            <TablaCreditosSesion creditos={creditosSesion} />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 13 — Abonos en una rejilla continua con su resumen por método */}
      {movCreditoAbonos.length > 0 ? (
        <>
          <SectionWrap className="space-y-1.5 sm:space-y-2">
            <SectionTitle>
              Abonos a crédito ({movCreditoAbonos.length})
            </SectionTitle>
            <AbonosCreditosTablaCompacta
              movs={movCreditoAbonos}
              ventas={ventas}
              empleadosCatalog={empleadosCatalog}
            />
            <IngresosPorMetodoTabla
              totales={creditoTotalesPorMetodo}
              totalGeneral={totalCreditoAbonos}
              titulo="Abonos a crédito por método"
              subtotalLabel="Subtotal abonos a crédito"
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 14 — Propinas cobradas */}
      {model.propinaTotal > 0 ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionPropinasCobradas
              propinas={model.propinas}
              totalPropinas={model.propinaTotal}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 15 — Rendimiento por empleado */}
      {model.rendimientoEmpleados.length > 0 ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionRendimientoEmpleado
              rows={model.rendimientoEmpleados}
              ventasActivasCount={model.ventasActivasCount}
              propinaTotal={model.propinaTotal}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* 16 — Propinas por empleado */}
      {model.propinaPorEmpleado.length > 0 ? (
        <>
          <SectionWrap className="space-y-1 sm:space-y-1.5 pt-1 sm:pt-2">
            <TicketSeccionPropinasPorEmpleado
              rows={model.propinaPorEmpleado}
              totalPropinas={model.propinaTotal}
            />
          </SectionWrap>
          <TicketDivider />
        </>
      ) : null}

      {/* Cierre final: auditoría digital. */}
      {!isParcial && cierre.transferenciasVerificacion ? (
        <>
          <ResumenAuditoriaDigital cierre={cierre} />
          <TicketDivider />
        </>
      ) : null}

      {isParcial ? (
        <div className="text-center pt-2 space-y-0.5 sm:pt-3 sm:space-y-1">
          <p className="text-[8px] sm:text-[10px] font-bold uppercase">
            Generado por: {cierre.closedByName || cierre.userId}
          </p>
          <p className="text-[7px] sm:text-[9px] text-gray-500">
            ID: {cierre.id.slice(0, 14).toUpperCase()}
          </p>
        </div>
      ) : null}
    </>
  );
}
